import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useColor } from '@/contexts/ColorContext';
import { useTranslation } from 'react-i18next';

export function ColorPicker() {
  const { t } = useTranslation('common');
  const [isExpanded, setIsExpanded] = useState(false);
  const { color, setColor } = useColor();
  const [inputValue, setInputValue] = useState(color.replace('#', ''));
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    if (/^[A-F0-9]*$/.test(value) && value.length <= 6) {
      setInputValue(value);
      if (value.length === 6) {
        setColor(`#${value}`);
      }
    }
  };

  const handleHexBlur = () => {
    if (inputValue.length !== 6) {
      setInputValue(color.replace('#', ''));
    }
  };

  useEffect(() => {
    setInputValue(color.replace('#', ''));
  }, [color]);

  // Measure content height for smooth animation
  const measureHeight = useCallback(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, []);

  useEffect(() => {
    measureHeight();
  }, [isExpanded, measureHeight]);

  return (
    <div className="flex flex-col gap-0 overflow-hidden rounded-lg bg-synergy-neutral-800">
      {/* Color bar trigger — always visible */}
      <button
        aria-label={t('parameter.openColorPicker')}
        onClick={() => setIsExpanded(!isExpanded)}
        className="group relative flex h-10 w-full items-center gap-3 px-3 transition-colors duration-150 ease-out [@media(hover:hover)]:hover:bg-synergy-neutral-700"
      >
        {/* Color swatch */}
        <div
          className="h-5 w-5 flex-shrink-0 rounded-[5px] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)]"
          style={{ backgroundColor: color }}
        />

        {/* Hex value */}
        <div className="flex items-center font-mono text-[12px] uppercase tracking-wide text-synergy-text-primary">
          <span className="text-synergy-neutral-400">#</span>
          <span>{inputValue || '------'}</span>
        </div>

        {/* Label */}
        <span className="ml-auto text-[10px] font-medium uppercase tracking-[0.08em] text-synergy-neutral-400">
          {t('parameter.color')}
        </span>
      </button>

      {/* Expandable picker area */}
      <div
        className="overflow-hidden transition-all duration-200 ease-out"
        style={{ height: isExpanded ? contentHeight : 0 }}
      >
        <div ref={contentRef} className="flex flex-col gap-3 px-3 pb-3 pt-1">
          {/* Color spectrum picker */}
          <div className="color-picker-wrapper overflow-hidden rounded-md">
            <HexColorPicker
              color={color}
              onChange={(newColor) => setColor(newColor.toUpperCase())}
              style={{ width: '100%', height: '120px' }}
            />
          </div>

          {/* Hex input row */}
          <div className="flex items-center gap-2">
            <div
              className="h-7 w-7 flex-shrink-0 rounded-md shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)]"
              style={{ backgroundColor: color }}
            />
            <div className="flex h-7 flex-1 items-center rounded-md bg-synergy-neutral-900 px-2">
              <span className="font-mono text-[11px] text-synergy-neutral-400">#</span>
              <input
                type="text"
                value={inputValue}
                onChange={handleHexChange}
                onBlur={handleHexBlur}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full bg-transparent px-1 font-mono text-[12px] uppercase tracking-wider text-synergy-text-primary outline-none selection:bg-synergy-blue/30"
                spellCheck="false"
                maxLength={6}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
